const API_BASE_URL = "https://srinivasafleet.com/api";

const getAuthToken = () => {
  return localStorage.getItem("token");
};

const request = async (url, options = {}) => {
  const token = getAuthToken();

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {}),
      ...(options.headers || {}),
    },
  });

  let data = null;

  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    const detail =
      data?.detail ||
      data?.message ||
      `Request failed with status ${response.status}`;

    if (response.status === 401) {
      throw new Error(
        "Authentication failed. Please login again."
      );
    }

    throw new Error(
      Array.isArray(detail)
        ? detail
            .map(
              (item) =>
                item?.msg ||
                String(item)
            )
            .join(", ")
        : String(detail)
    );
  }

  return data;
};

const getResponseData = (result) => {
  if (
    result &&
    result.data !== undefined
  ) {
    return result.data;
  }

  return result;
};

const buildContacts = (data = {}) => {
  if (Array.isArray(data.contacts)) {
    return data.contacts.filter(Boolean);
  }

  const contacts = [];

  if (data.supplier_mobile) {
    contacts.push(data.supplier_mobile);
  }

  if (data.driver_mobile) {
    contacts.push(data.driver_mobile);
  }

  if (data.supplier_phone) {
    contacts.push(data.supplier_phone);
  }

  if (data.driver_phone) {
    contacts.push(data.driver_phone);
  }

  return [
    ...new Set(
      contacts.filter(Boolean)
    ),
  ];
};

const normalizeAbsentRecord = (record) => {
  const data =
    record?.data &&
    !Array.isArray(record.data)
      ? record.data
      : record || {};

  return {
    id: data.id ?? null,

    date:
      data.absent_date ||
      data.date ||
      "",

    absentDate:
      data.absent_date ||
      data.date ||
      "",

    cabNumber:
      data.vehicle_registration_number ||
      data.cab_number ||
      data.vehicle_display_number ||
      "",

    vehicleRegistrationNumber:
      data.vehicle_registration_number ||
      data.cab_number ||
      "",

    vehicleDisplayNumber:
      data.vehicle_display_number ||
      "",

    supplier:
      data.supplier_name ||
      data.supplier ||
      "",

    supplierName:
      data.supplier_name ||
      data.supplier ||
      "",

    supplierId:
      data.supplier_id ?? null,

    driver:
      data.driver_name ||
      data.driver ||
      "",

    driverName:
      data.driver_name ||
      data.driver ||
      "",

    driverId:
      data.driver_id ?? null,

    contacts:
      buildContacts(data),

    supplierMobile:
      data.supplier_mobile ||
      data.supplier_phone ||
      "",

    driverMobile:
      data.driver_mobile ||
      data.driver_phone ||
      "",

    vehicleType:
      data.vehicle_type ||
      "",

    year:
      data.year ??
      data.year_of_make ??
      "",

    vehicleId:
      data.vehicle_id ?? null,

    vehicleAssignmentId:
      data.vehicle_assignment_id ?? null,

    remarks:
      data.remarks ||
      "",

    smsSent:
      Boolean(data.sms_sent),

    smsSentAt:
      data.sms_sent_at ||
      null,

    createdBy:
      data.created_by ??
      null,

    createdByName:
      data.created_by_name ||
      "",

    updatedBy:
      data.updated_by_name ||
      data.updated_by ||
      "",

    updatedByName:
      data.updated_by_name ||
      "",

    updatedAt:
      data.updated_at ||
      data.created_at ||
      "",

    createdAt:
      data.created_at ||
      "",

    raw:
      data,
  };
};

const normalizeSearchResult = (result) => {
  const data = getResponseData(result);

  if (!data) {
    return null;
  }

  if (Array.isArray(data)) {
    if (!data.length) {
      return null;
    }

    return normalizeAbsentRecord(data[0]);
  }

  return normalizeAbsentRecord(data);
};

export const searchAbsentCab = async ({
  cabLast4,
  absentDate,
}) => {
  if (!cabLast4) {
    throw new Error(
      "Please enter the last 4 digits of the cab number."
    );
  }

  if (
    !/^\d{4}$/.test(
      String(cabLast4)
    )
  ) {
    throw new Error(
      "Cab last 4 digits must contain exactly 4 numbers."
    );
  }

  if (!absentDate) {
    throw new Error(
      "Please select the absent date."
    );
  }

  const result = await request(
    "/absent-cab-followups/search-cab",
    {
      method: "POST",
      body: JSON.stringify({
        cab_last_4: String(cabLast4),
        absent_date: absentDate,
      }),
    }
  );

  return normalizeSearchResult(result);
};

export const getAbsentCabFollowups = async ({
  fromDate = "",
  toDate = "",
} = {}) => {
  const params = new URLSearchParams();

  if (fromDate) {
    params.set(
      "from_date",
      fromDate
    );
  }

  if (toDate) {
    params.set(
      "to_date",
      toDate
    );
  }

  const query = params.toString();

  const result = await request(
    `/absent-cab-followups/${
      query ? `?${query}` : ""
    }`
  );

  const data = getResponseData(result);

  if (!Array.isArray(data)) {
    return [];
  }

  return data.map(
    normalizeAbsentRecord
  );
};

export const createAbsentCabFollowup = async (
  payload
) => {
  if (!payload?.absentDate) {
    throw new Error(
      "Absent date is required."
    );
  }

  if (!payload?.vehicleId) {
    throw new Error(
      "Vehicle is required."
    );
  }

  if (!payload?.vehicleAssignmentId) {
    throw new Error(
      "Vehicle assignment is required."
    );
  }

  const result = await request(
    "/absent-cab-followups/",
    {
      method: "POST",
      body: JSON.stringify({
        absent_date:
          payload.absentDate,

        vehicle_id:
          payload.vehicleId,

        vehicle_assignment_id:
          payload.vehicleAssignmentId,

        remarks:
          payload.remarks || "",
      }),
    }
  );

  return normalizeAbsentRecord(
    getResponseData(result)
  );
};

export const updateAbsentCabFollowup = async (
  followupId,
  payload
) => {
  if (!followupId) {
    throw new Error(
      "Follow-up ID is required."
    );
  }

  const result = await request(
    `/absent-cab-followups/${followupId}`,
    {
      method: "PUT",
      body: JSON.stringify({
        remarks:
          payload?.remarks || "",
      }),
    }
  );

  return normalizeAbsentRecord(
    getResponseData(result)
  );
};

export const markAbsentSmsSent = async (
  followupId
) => {
  if (!followupId) {
    throw new Error(
      "Follow-up ID is required."
    );
  }

  const result = await request(
    `/absent-cab-followups/${followupId}/sms-sent`,
    {
      method: "PATCH",
    }
  );

  return normalizeAbsentRecord(
    getResponseData(result)
  );
};

export const deleteAbsentCabFollowup = async (
  followupId
) => {
  if (!followupId) {
    throw new Error(
      "Follow-up ID is required."
    );
  }

  return request(
    `/absent-cab-followups/${followupId}`,
    {
      method: "DELETE",
    }
  );
};

export const downloadAbsentFollowupReport = async ({
  fromDate = "",
  toDate = "",
} = {}) => {
  const params = new URLSearchParams();

  if (fromDate) {
    params.set(
      "from_date",
      fromDate
    );
  }

  if (toDate) {
    params.set(
      "to_date",
      toDate
    );
  }

  const query = params.toString();

  const token = getAuthToken();

  const response = await fetch(
    `${API_BASE_URL}/absent-cab-followups/report${
      query ? `?${query}` : ""
    }`,
    {
      method: "GET",
      headers: {
        Accept: "*/*",
        ...(token
          ? {
              Authorization:
                `Bearer ${token}`,
            }
          : {}),
      },
    }
  );

  if (!response.ok) {
    let detail =
      `Report download failed with status ${response.status}`;

    try {
      const data =
        await response.json();

      detail =
        data?.detail ||
        data?.message ||
        detail;
    } catch {}

    if (response.status === 401) {
      throw new Error(
        "Authentication failed. Please login again."
      );
    }

    throw new Error(
      Array.isArray(detail)
        ? detail
            .map(
              (item) =>
                item?.msg ||
                String(item)
            )
            .join(", ")
        : String(detail)
    );
  }

  const blob =
    await response.blob();

  const contentDisposition =
    response.headers.get(
      "content-disposition"
    );

  let filename =
    "Absent_Cab_Followup_Report.csv";

  if (contentDisposition) {
    const match =
      contentDisposition.match(
        /filename="?([^"]+)"?/i
      );

    if (match?.[1]) {
      filename =
        match[1];
    }
  }

  const url =
    window.URL.createObjectURL(
      blob
    );

  const link =
    document.createElement("a");

  link.href = url;
  link.download = filename;

  document.body.appendChild(
    link
  );

  link.click();

  document.body.removeChild(
    link
  );

  window.URL.revokeObjectURL(
    url
  );

  return true;
};

export default {
  searchAbsentCab,
  getAbsentCabFollowups,
  createAbsentCabFollowup,
  updateAbsentCabFollowup,
  markAbsentSmsSent,
  deleteAbsentCabFollowup,
  downloadAbsentFollowupReport,
};